import { ReactElement, ReactNode } from 'react'
import { Box, Chip, Typography } from '@mui/material'
import type { DesignForcesOnSupportDTO } from '@api/fastAPI.schemas'
import { DenseTable } from '@ui2/structure'
import LoadCaseCombinationFormula from '../../Results2DetailPane/components/LoadCaseCombinationFormula'
import {
  AnchorLoadCategory,
  anchorLoadCategoryLabel,
  formatNewtonsAsKn,
  formatNumber,
  getDesignForcePerCategory,
  PredictedAnchorCheck,
} from '../utils'

interface Props {
  designForces?: DesignForcesOnSupportDTO
  check?: AnchorCheckOnSupport
  /** Sum of the persisted manual loads in Newtons (η is computed from the saved state). */
  savedManualSum: number
  /**
   * Manual-load sum the table shows, in Newtons — the draft rows while they are valid, otherwise
   * the saved sum.
   */
  draftManualSum: number
  /** True while the table previews a valid, unsaved manual-load draft. */
  isDirty: boolean
  /**
   * Live per-category prediction of the anchor product the backend would pick for the current
   * draft forces. When present, the Anker/R_d/η rows follow the prediction instead of the saved
   * check (walls only — columns/rips have no prioritisation to predict from).
   */
  predictions?: Partial<Record<AnchorLoadCategory, PredictedAnchorCheck>>
}

interface CategoryColumn {
  category: AnchorLoadCategory
  combination?: LoadCaseCombination
  /** Decisive design force of the category in Newtons, manual loads not included. */
  designForce: number
  /** Tensile part of `designForce` (head loads, anchors above) — only with per-support forces. */
  tensile?: number
  /** Stabilizing part of `designForce` (relieving loads, negative) — only with per-support forces. */
  stabilizing?: number
  resistance?: number
  /** Product shown in the Anker row — the live prediction, or the saved check's product. */
  anchorName?: string
  prediction?: PredictedAnchorCheck
}

/**
 * The combined "Resultierende Design-Zuglast & Nachweis" table (transposed, one column per
 * Wind/Erdbeben category) — mirrors the tensile-anchor export, with the manual-load sum and the
 * anchor check folded into one table.
 */
const AnchorDesignForceCheckTable = ({
  designForces,
  check,
  savedManualSum,
  draftManualSum,
  isDirty,
  predictions,
}: Props): ReactElement => {
  const perCategory = getDesignForcePerCategory(designForces)

  const columns = (['Wind', 'EarthQuake'] as const).flatMap<CategoryColumn>(category => {
    const prediction = predictions?.[category]
    const savedResistance =
      category === 'Wind' ? check?.used_anchor.r_d_wind : check?.used_anchor.r_d_earthquake
    const decisive = perCategory[category]
    if (decisive) {
      return [
        {
          category,
          combination: decisive.load_combination as unknown as LoadCaseCombination,
          designForce: decisive.total,
          tensile: decisive.tensile,
          stabilizing: decisive.stabilizing,
          resistance: prediction?.resistance ?? savedResistance,
          anchorName: prediction?.connection.name ?? check?.used_anchor.name,
          prediction,
        },
      ]
    }
    // Without the per-support design forces, fall back to the decisive check only.
    if (check && (check.load_category ?? 'Wind') === category) {
      return [
        {
          category,
          combination: check.design_force.load_combination,
          designForce: check.design_force.force.z - savedManualSum,
          resistance: prediction?.resistance ?? savedResistance,
          anchorName: prediction?.connection.name ?? check.used_anchor.name,
          prediction,
        },
      ]
    }
    return []
  })

  if (columns.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
        Keine Design-Zuglasten verfügbar — bitte Ergebnisse berechnen.
      </Typography>
    )
  }

  // No tension left in the category (F_d,neu ≤ 0) → no anchor needed. The preview endpoint
  // returns no check for such a category, so the Anker/R_d cells must not fall back to the
  // saved product either — they show "–", like other overpressed anchors.
  const needsAnchor = (column: CategoryColumn): boolean => column.designForce + draftManualSum > 0

  // Live utilization: recomputed instantly from the current (unsaved) manual-load sum and the
  // resistance of the predicted (or currently chosen) anchor.
  const utilizationOf = (column: CategoryColumn): number | undefined => {
    if (!needsAnchor(column)) return 0
    if (column.prediction) return column.prediction.utilization
    if (!column.resistance) return undefined
    return (column.designForce + draftManualSum) / column.resistance
  }

  const hasComputableUtilization = columns.some(column => utilizationOf(column) !== undefined)
  const decisiveCategory: AnchorLoadCategory | undefined = hasComputableUtilization
    ? columns.reduce((max, column) =>
        (utilizationOf(column) ?? 0) > (utilizationOf(max) ?? 0) ? column : max,
      ).category
    : check?.load_category === 'EarthQuake' || check?.load_category === 'Wind'
    ? check.load_category
    : columns[0]?.category

  const headers: ReactNode[] = [
    '',
    ...columns.map(column => (
      <Box key={column.category} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        {anchorLoadCategoryLabel[column.category]}
        {column.category === decisiveCategory && (
          <Chip label="maßgebend" size="small" sx={{ height: 16, fontSize: 10 }} />
        )}
      </Box>
    )),
  ]

  // The tensile/stabilizing split only exists with the per-support design forces.
  const breakdownRows: ReactNode[][] = columns.some(column => column.tensile !== undefined)
    ? [
        [
          <span key="fdt">
            F<sub>d,Zug</sub> aus Wind/Erdbeben [kN]
          </span>,
          ...columns.map(column => formatNewtonsAsKn(column.tensile)),
        ],
        [
          <span key="fds">
            F<sub>d,stb</sub> stabilisierend (inkl. γ·ψ) [kN]
          </span>,
          ...columns.map(column => formatNewtonsAsKn(column.stabilizing)),
        ],
      ]
    : []

  const rows: ReactNode[][] = [
    [
      'Lastfallkombination',
      ...columns.map(column => (
        <LoadCaseCombinationFormula key={column.category} lcc={column.combination} />
      )),
    ],
    ...breakdownRows,
    [
      <span key="fd">
        F<sub>d</sub> resultierend [kN]
      </span>,
      ...columns.map(column => formatNewtonsAsKn(column.designForce)),
    ],
    ['Σ manuelle Lasten [kN]', ...columns.map(() => formatNewtonsAsKn(draftManualSum))],
    [
      <Box component="span" key="fdneu" sx={{ fontWeight: 700 }}>
        F<sub>d,neu</sub> [kN]
      </Box>,
      ...columns.map(column => (
        <Box component="span" key={column.category} sx={{ fontWeight: 700 }}>
          {formatNewtonsAsKn(column.designForce + draftManualSum)}
        </Box>
      )),
    ],
    ['Anker', ...columns.map(column => (needsAnchor(column) ? column.anchorName ?? '–' : '–'))],
    [
      <span key="rd">
        R<sub>d</sub> [kN]
      </span>,
      ...columns.map(column =>
        formatNewtonsAsKn(needsAnchor(column) ? column.resistance : undefined),
      ),
    ],
    [
      'Ausnutzung η',
      ...columns.map(column => {
        const utilization = utilizationOf(column)
        return (
          <Box
            component="span"
            key={column.category}
            sx={{
              fontWeight: 700,
              color: utilization !== undefined && utilization > 1 ? 'error.main' : 'success.main',
            }}
          >
            {formatNumber(utilization ?? 0)}
          </Box>
        )
      }),
    ],
  ]

  return (
    <Box sx={{ pb: 1 }}>
      <DenseTable headers={headers} rows={rows} emptyMessage="" />
      {isDirty && (
        <Typography variant="caption" color="text.secondary">
          {predictions
            ? 'Vorschau — Anker und Nachweis werden beim Speichern verbindlich ermittelt.'
            : 'η bezogen auf den aktuell gewählten Anker — beim Speichern wird der Anker ggf. neu gewählt.'}
        </Typography>
      )}
    </Box>
  )
}

export default AnchorDesignForceCheckTable
